/**
 * /payroll/year-end/1099-nec/:year — 1099-NEC batch generation + IRIS transmit.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { api, getToken } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/year-end_/1099-nec/$year")({
  component: Form1099NECBatchPage,
});

interface Contractor {
  id: string;
  business_name: string | null;
  first_name: string | null;
  last_name: string | null;
  address_state: string | null;
  is_active: boolean;
  w9_on_file: boolean;
}

function Form1099NECBatchPage() {
  const { year } = Route.useParams();
  const yr = Number(year);
  const qc = useQueryClient();
  const [confirm, setConfirm] = useState(false);
  const [result, setResult] = useState<any | null>(null);

  const { data: contractors = [], isLoading } = useQuery({
    queryKey: ["payroll", "contractors"],
    queryFn: async () => (await api(`/api/payroll/contractors`)) as Contractor[],
  });

  const transmit = useMutation({
    mutationFn: async () => api(`/api/payroll/year-end/1099-nec/${yr}/transmit`, { method: "POST" }),
    onSuccess: (r) => {
      setResult(r);
      setConfirm(false);
      qc.invalidateQueries({ queryKey: ["payroll", "year-end"] });
    },
  });

  const downloadPdf = (id: string, name: string, copy = "B") => {
    const url = `/api/payroll/year-end/1099-nec/${yr}/${id}.pdf?copy=${copy}`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = u;
        a.download = `1099-NEC-${yr}-${name}.pdf`;
        a.click();
        URL.revokeObjectURL(u);
      });
  };

  const recipientName = (c: Contractor) =>
    c.business_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.id;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      <Stack direction="row" alignItems="center" spacing={1} mb={1}>
        <IconButton component={Link} to="/payroll/year-end" size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="caption" color="text.secondary">Back to Year-End</Typography>
      </Stack>
      <Typography variant="h4" fontWeight={700} mb={1}>
        1099-NEC Batch — Tax Year {yr}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={1}>
        Required for any contractor paid <strong>≥ $600</strong> for services. The IRS threshold is
        applied automatically — recipients under $600 are excluded from transmission.
      </Typography>
      <Box sx={{ mb: 3 }}>
        <HelpHint docPath="payroll-year-end/filing-1099-nec.md" label="1099-NEC filing guide" />
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <Button variant="contained" startIcon={<CloudUploadIcon />} onClick={() => setConfirm(true)}
                disabled={contractors.length === 0}>
          Transmit all 1099-NECs to IRIS
        </Button>
      </Stack>

      {result && (
        <Alert severity={result.status === "rejected" ? "error" : result.status === "dry_run" ? "warning" : "success"} sx={{ mb: 3 }}>
          <strong>{result.status?.toUpperCase()}</strong> — submission <code>{result.submission_id}</code>, {result.record_count} records.
          {result.error && <Box sx={{ mt: 1 }}>{result.error}</Box>}
        </Alert>
      )}

      {isLoading ? <CircularProgress /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Recipient</TableCell>
                <TableCell>State</TableCell>
                <TableCell>W-9 on file</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {contractors.map((c) => (
                <TableRow key={c.id}>
                  <TableCell><Typography variant="body2" fontWeight={500}>{recipientName(c)}</Typography></TableCell>
                  <TableCell>{c.address_state || "—"}</TableCell>
                  <TableCell>
                    <Chip size="small" label={c.w9_on_file ? "Yes" : "No"} color={c.w9_on_file ? "success" : "warning"} />
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" startIcon={<PictureAsPdfIcon />} onClick={() => downloadPdf(c.id, recipientName(c), "B")}>
                      Copy B
                    </Button>
                    <Button size="small" onClick={() => downloadPdf(c.id, recipientName(c), "C")}>Copy C</Button>
                  </TableCell>
                </TableRow>
              ))}
              {contractors.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No contractors found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={confirm} onClose={() => setConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Transmit 1099-NEC Batch to IRS IRIS?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Submits all contractors paid ≥ $600 in {yr}. Recipients under the threshold are excluded.
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Test mode validates without transmitting. Set <code>iris_environment=production</code> for live filing.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => transmit.mutate()} disabled={transmit.isPending}>
            {transmit.isPending ? <CircularProgress size={18} /> : "Transmit"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
